import Bugsnag from "@bugsnag/js";
import { client } from "@nju/http-client";
import { ok } from "@nju/result";
import { COMMITMENT_BALANCE_ID, COMMITMENT_PENALTY_FEE_ID } from "../consts";
import {
  is5G,
  isAccess,
  isAdditionalTask,
  isCommitmentResponse,
  isDataPackage,
  isFSecure,
  isInternet,
  isMobileAccess,
  isModem,
  isSfh,
  isSimCard,
  isTelevision,
  isVoicePackage,
  mapInternetResponse,
} from "../utils";
import {
  ICountryBalance,
  IDataPackage,
  IFetchOrderProductsResult,
  IRoamingBalance,
  IVoicePackage,
} from "./types";
import { IApiDataPackage, serverResponse } from "./validators";

export async function fetchOrderProducts(
  userId?: string | null,
): Promise<IFetchOrderProductsResult> {
  const result = await client(
    `${process.env.NEXT_PUBLIC_PUBLIC_GATEWAY_HOST}/products`,
    {
      headers: {
        apikey: process.env.NEXT_PUBLIC_APIKEY || "unknown",
      },
      validator: serverResponse,
      searchParams: {
        ...(userId && { userId }),
      },
    },
  );

  if (result.isError) {
    Bugsnag.notify(result.err);
    return result;
  }

  // ftth
  const modem = result.value.find(isModem);
  const internet = mapInternetResponse(result.value.find(isInternet));
  const access = result.value.find(isAccess);
  const additionalTask = result.value.find(isAdditionalTask);
  const commitmentResponseData = result.value.find(isCommitmentResponse);
  const sfh = result.value.find(isSfh);
  const fSecure = result.value.find(isFSecure);

  const ftthProducts = [
    modem,
    internet,
    access,
    additionalTask,
    commitmentResponseData,
  ];

  const fSecureProducts = [fSecure];

  // mobile
  const dataPackage = result.value.find(isDataPackage);
  const simCard = result.value.find(isSimCard);
  const voicePackage = result.value.find(isVoicePackage);
  const mobileAccess = result.value.find(isMobileAccess);
  const television = result.value.find(isTelevision);
  const _5g = result.value.find(is5G);

  const freeDataPackage = result.value.find((product) => {
    if ("specificationId" in product) {
      return (
        product.specificationId === "DATA_PACKAGE" &&
        product.offeringId === "NJUExtraDataPackageAggr"
      );
    }

    return;
  });

  const birthdayPackage = result.value.find((product) => {
    if ("specificationId" in product) {
      return (
        product.specificationId === "DATA_PACKAGE" &&
        product.offeringId === "NJUExtraDataPackageAggr"
      );
    }

    return;
  });

  const internationalCalls = result.value.filter((product) => {
    if ("specificationId" in product) {
      return (
        product.specificationId === "VOICE_PACKAGE" &&
        product.offeringCategory === "INTERNATIONAL"
      );
    }

    return;
  });

  const roamingCalls = result.value.filter((product) => {
    if ("specificationId" in product) {
      return (
        product.specificationId === "VOICE_PACKAGE" &&
        product.offeringCategory === "ROAMING"
      );
    }

    return;
  });

  const roamingSTDEXODataPackage = result.value.filter((product) => {
    if ("specificationId" in product) {
      return (
        product.specificationId === "DATA_PACKAGE" &&
        product.offeringCategory === "ROAMING"
      );
    }

    return;
  }) as IApiDataPackage[] | undefined;

  const mobileProducts = [
    dataPackage,
    simCard,
    voicePackage,
    mobileAccess,
    _5g,
    television,
  ];

  const isProductsUndefined = result.value === undefined;

  const commitmentCounter = commitmentResponseData?.balances.find(
    ({ balanceId }) => {
      return balanceId === COMMITMENT_BALANCE_ID;
    },
  );

  const commitmentPenaltyFee = commitmentResponseData?.balances.find(
    ({ balanceId }) => {
      return balanceId === COMMITMENT_PENALTY_FEE_ID;
    },
  );

  const isLoyaltyOffer =
    commitmentResponseData?.offeringId === "NJU_FTTH_COMMITMENT";

  const commitment = commitmentResponseData
    ? {
        ...commitmentResponseData,
        isLoyaltyOffer,
        willBreakLoyaltyOnResignation:
          isLoyaltyOffer &&
          commitmentCounter?.currentValue !== undefined &&
          Number.parseInt(commitmentCounter.currentValue) > 0,
        counter: commitmentCounter,
        penaltyFee: commitmentPenaltyFee?.currentValue
          ? Number.parseInt(commitmentPenaltyFee.currentValue)
          : undefined,
      }
    : undefined;

  const hasFtthProducts = !ftthProducts.every((element) => {
    return element === undefined;
  });

  const hasFSecureProducts = !fSecureProducts.every((element) => {
    return element === undefined;
  });

  const hasMobileProducts = !mobileProducts.every((element) => {
    return element === undefined;
  });

  const mappedDataPackage: IDataPackage | undefined = dataPackage
    ? ({
        ...dataPackage,
        country: dataPackage.balances.find((balance) => {
          return (
            balance.balanceId && ["21267", "21018"].includes(balance.balanceId)
          );
        }) as ICountryBalance | undefined,
        roaming: dataPackage.balances.find((balance) => {
          return (
            balance.balanceId && ["21268", "21129"].includes(balance.balanceId)
          );
        }) as IRoamingBalance | undefined,
        roamingSTDPackage: roamingSTDEXODataPackage?.find((data) => {
          return data.zone === "STD";
        }) as IApiDataPackage | undefined,
        roamingEXOPackage: roamingSTDEXODataPackage?.find((data) => {
          return data.zone === "EXO";
        }) as IApiDataPackage | undefined,
        roamingBREXITPackage: roamingSTDEXODataPackage?.find((data) => {
          return data.zone === "BREXIT";
        }) as IApiDataPackage | undefined,
        roamingHTPackage: roamingSTDEXODataPackage?.find((data) => {
          return data.zone === "HT";
        }) as IApiDataPackage | undefined,
        birthdayPackage: birthdayPackage as IApiDataPackage | undefined,
        freeDataPackage: freeDataPackage as IApiDataPackage | undefined,
      } as IDataPackage | undefined)
    : undefined;

  const mappedVoicePackage: IVoicePackage | undefined = voicePackage
    ? ({
        ...voicePackage,
        internationalCalls,
        roamingCalls,
      } as IVoicePackage | undefined)
    : undefined;

  return ok({
    ftthProducts: {
      sfh,
      modem,
      internet,
      access,
      additionalTask,
      commitment,
      hasProducts: hasFtthProducts,
      hasNoProducts: !hasFtthProducts,
      isProductsUndefined,
    },
    mobileProducts: {
      dataPackage: mappedDataPackage,
      simCard,
      voicePackage: mappedVoicePackage,
      mobileAccess,
      _5g,
      hasProducts: hasMobileProducts,
      hasNoProducts: !hasMobileProducts,
      isProductsUndefined,
      television,
    },
    fSecureProducts: {
      isProductsUndefined,
      hasProducts: hasFSecureProducts,
      hasNoProducts: !hasFSecureProducts,
      fSecure,
    },
  });
}
